// -----------------------------------------------------------------------------
// ui/timeline/interactions.ts — pointer handling for the timeline canvas.
//
// Translates clicks/drags on the canvas into edits: select a clip, move it (and
// across tracks), trim either edge (with snapping to the frame grid and clamping
// to the source's available range), scrub the playhead on the ruler, and remove a
// track via its ✕. Live feedback is drawn through the view's transient `drag`
// state; the actual undoable Command is dispatched once on pointer-up.
// -----------------------------------------------------------------------------
import { MoveClipCommand, TrimClipCommand } from '../../core/commands/clip-commands';
import { RemoveTrackCommand } from '../../core/commands/track-commands';
import { type MediaClip, clipEnd, locateClip } from '../../core/model';
import { snapToFrame } from '../../shared/time';
import type { EditorContext } from '../context';
import { placeAsset } from '../place-clip';
import { type TimelineView } from './timeline';

interface DragOrigin {
  mode: 'move' | 'trim-start' | 'trim-end';
  startSec: number;
  inSec: number;
  endSec: number;
  trackIndex: number;
  grabOffsetSec: number; // pointer time − clip start, for moves
  assetDuration: number; // source length, 0 = unbounded (still image / text)
  isMedia: boolean;
}

export function attachTimelineInteractions(view: TimelineView, ctx: EditorContext): void {
  const canvas = view.canvas;
  let origin: DragOrigin | null = null;
  let scrubbing = false;
  // Times a dragged clip should snap to (computed when a drag starts).
  let snapTargets: number[] = [];

  const minDur = () => 1 / ctx.store.state.project.fps;

  // Collect snap targets: timeline start, the playhead, and every OTHER clip's
  // edges (so clips align edge-to-edge and to the cursor position).
  function buildSnapTargets(draggedClipId: string): number[] {
    const targets = [0, ctx.player.getTime()];
    for (const track of ctx.store.state.project.tracks) {
      for (const c of track.clips) {
        if (c.id === draggedClipId) continue;
        targets.push(c.startSec, clipEnd(c));
      }
    }
    return targets;
  }

  function localPos(e: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  // setPointerCapture can throw if the pointer is already gone; never let that
  // bubble up as an uncaught error.
  function capture(pointerId: number): void {
    try {
      canvas.setPointerCapture(pointerId);
    } catch {
      /* pointer no longer active — fine */
    }
  }

  canvas.addEventListener('pointerdown', (e) => {
    const { x, y } = localPos(e);
    const hit = view.hitTest(x, y);

    if (hit.kind === 'ruler') {
      scrubbing = true;
      ctx.player.seek(view.xToTime(x));
      capture(e.pointerId);
      return;
    }
    if (hit.kind === 'remove-track') {
      // Instantaneous action — no pointer capture needed (it's not a drag). The
      // command itself clears any selection that lived on the removed track.
      const track = ctx.store.state.project.tracks[hit.trackIndex];
      if (track) {
        ctx.store.dispatch(new RemoveTrackCommand(track.id));
        ctx.setStatus(`Removed track “${track.name}”.`);
      }
      return;
    }
    if (hit.kind === 'empty' || hit.kind === 'outside') {
      ctx.store.select(null);
      return;
    }

    // hit.kind === 'clip'
    ctx.store.select(hit.clipId);
    const loc = locateClip(ctx.store.state.project, hit.clipId);
    if (!loc) return;
    const clip = loc.clip;
    const isMedia = clip.kind !== 'text';
    const asset = isMedia ? ctx.store.getAsset((clip as MediaClip).assetId) : undefined;

    origin = {
      mode: hit.edge === 'start' ? 'trim-start' : hit.edge === 'end' ? 'trim-end' : 'move',
      startSec: clip.startSec,
      inSec: isMedia ? (clip as MediaClip).inSec : 0,
      endSec: clipEnd(clip),
      trackIndex: hit.trackIndex,
      grabOffsetSec: view.xToTime(x) - clip.startSec,
      assetDuration: asset?.durationSec ?? 0,
      isMedia,
    };
    view.drag = {
      clipId: hit.clipId,
      mode: origin.mode,
      startSec: clip.startSec,
      durationSec: clip.durationSec,
      inSec: origin.inSec,
      trackIndex: hit.trackIndex,
    };
    snapTargets = buildSnapTargets(hit.clipId);
    capture(e.pointerId);
    view.draw();
  });

  canvas.addEventListener('pointermove', (e) => {
    const { x, y } = localPos(e);

    if (scrubbing) {
      ctx.player.seek(view.xToTime(x));
      return;
    }
    if (!origin || !view.drag) return;

    const fps = ctx.store.state.project.fps;
    const pointerTime = snapToFrame(view.xToTime(x), fps);
    const min = minDur();
    // Snap threshold: ~8px worth of time at the current zoom.
    const snapThreshold = 8 / view.pxPerSecond;

    if (origin.mode === 'move') {
      const trackCount = ctx.store.state.project.tracks.length;
      const duration = origin.endSec - origin.startSec;
      let newStart = Math.max(0, snapToFrame(pointerTime - origin.grabOffsetSec, fps));
      // Snap whichever edge (start or end) is closest to a target.
      const snapStart = nearest(newStart, snapTargets);
      const snapEnd = nearest(newStart + duration, snapTargets);
      if (snapStart.dist <= snapEnd.dist && snapStart.dist < snapThreshold) {
        newStart = snapStart.value;
        view.snapLine = snapStart.value;
      } else if (snapEnd.dist < snapThreshold) {
        newStart = snapEnd.value - duration;
        view.snapLine = snapEnd.value;
      } else {
        view.snapLine = null;
      }
      newStart = Math.max(0, newStart);
      const newTrack = Math.min(Math.max(0, view.trackIndexAtY(y)), trackCount - 1);
      view.drag.startSec = newStart;
      view.drag.trackIndex = newTrack;
    } else if (origin.mode === 'trim-start') {
      const lower = origin.isMedia ? Math.max(0, origin.startSec - origin.inSec) : 0;
      const upper = origin.endSec - min;
      let newStart = Math.min(Math.max(pointerTime, lower), upper);
      const snap = nearest(newStart, snapTargets);
      if (snap.dist < snapThreshold) {
        newStart = Math.min(Math.max(snap.value, lower), upper);
        view.snapLine = newStart;
      } else {
        view.snapLine = null;
      }
      view.drag.startSec = newStart;
      view.drag.durationSec = origin.endSec - newStart;
      view.drag.inSec = origin.isMedia ? origin.inSec + (newStart - origin.startSec) : 0;
    } else {
      // trim-end
      const lower = origin.startSec + min;
      const upper =
        origin.isMedia && origin.assetDuration > 0
          ? origin.startSec + (origin.assetDuration - origin.inSec)
          : Number.POSITIVE_INFINITY;
      let newEnd = Math.min(Math.max(pointerTime, lower), upper);
      const snap = nearest(newEnd, snapTargets);
      if (snap.dist < snapThreshold) {
        newEnd = Math.min(Math.max(snap.value, lower), upper);
        view.snapLine = newEnd;
      } else {
        view.snapLine = null;
      }
      view.drag.durationSec = newEnd - origin.startSec;
    }
    view.draw();
  });

  function endDrag(e: PointerEvent): void {
    if (scrubbing) {
      scrubbing = false;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      return;
    }
    if (!origin || !view.drag) return;
    const drag = view.drag;

    if (origin.mode === 'move') {
      const track = ctx.store.state.project.tracks[drag.trackIndex];
      if (track) ctx.store.dispatch(new MoveClipCommand(drag.clipId, track.id, drag.startSec));
    } else {
      ctx.store.dispatch(
        new TrimClipCommand(drag.clipId, {
          startSec: drag.startSec,
          inSec: drag.inSec,
          durationSec: drag.durationSec,
        }),
      );
    }

    origin = null;
    view.drag = null;
    view.snapLine = null;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    view.draw();
  }

  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  // ---- drag-and-drop from the media panel ------------------------------------
  canvas.addEventListener('dragover', (e) => {
    if (!e.dataTransfer?.types.includes('application/x-zeroframe-asset')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  });
  canvas.addEventListener('drop', (e) => {
    const assetId = e.dataTransfer?.getData('application/x-zeroframe-asset');
    if (!assetId) return;
    e.preventDefault();
    const asset = ctx.store.getAsset(assetId);
    if (!asset) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const fps = ctx.store.state.project.fps;
    const startSec = snapToFrame(Math.max(0, view.xToTime(x)), fps);
    const track = ctx.store.state.project.tracks[view.trackIndexAtY(y)];
    placeAsset(ctx, asset, { startSec, trackId: track?.id });
  });

  // Cursor affordance: resize arrows on edges, grab on bodies.
  canvas.addEventListener('pointermove', (e) => {
    if (origin || scrubbing) return;
    const { x, y } = localPos(e);
    const hit = view.hitTest(x, y);
    canvas.style.cursor =
      hit.kind === 'clip'
        ? hit.edge === 'body'
          ? 'grab'
          : 'ew-resize'
        : hit.kind === 'ruler'
          ? 'pointer'
          : hit.kind === 'remove-track'
            ? 'pointer'
            : 'default';
  });
}

// Nearest snap target to `value` plus its distance (Infinity when no targets).
function nearest(value: number, targets: number[]): { value: number; dist: number } {
  let best = value;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const target of targets) {
    const dist = Math.abs(target - value);
    if (dist < bestDist) {
      bestDist = dist;
      best = target;
    }
  }
  return { value: best, dist: bestDist };
}
